import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  invoicesApi,
  EmailConfig,
  EmailSettings,
  EmailTemplate,
  EmailVariable
} from '../lib/api';
import {
  X, Mail, Save, RotateCcw, Loader2, Check, AlertCircle,
  FileText, Globe, Info
} from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

// Flag icons for languages
const langFlags: Record<string, string> = {
  es: '/flags/es.svg',
  pt: '/flags/pt.svg',
  en: '/flags/gb.svg',
  fr: '/flags/fr.svg',
  de: '/flags/de.svg',
};

export default function InvoiceEmailConfigModal({ isOpen, onClose }: Props) {
  const queryClient = useQueryClient();

  // Local state
  const [selectedLang, setSelectedLang] = useState<string>('es');
  const [settings, setSettings] = useState<EmailSettings>({
    attach_pdf: true,
    from_email: '',
    from_name: ''
  });
  const [currentTemplate, setCurrentTemplate] = useState<{ subject: string; body: string }>({
    subject: '',
    body: ''
  });
  const [hasSettingsChanges, setHasSettingsChanges] = useState(false);
  const [hasTemplateChanges, setHasTemplateChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  // Query
  const { data: configData, isLoading, refetch } = useQuery({
    queryKey: ['emailConfig'],
    queryFn: invoicesApi.getEmailConfig,
    enabled: isOpen,
  });

  const config = configData?.config;

  // Update local state when config loads
  useEffect(() => {
    if (config) {
      setSettings(config.settings);
      // Find template for selected language
      const template = config.templates.find(t => t.lang_iso === selectedLang);
      if (template) {
        setCurrentTemplate({ subject: template.subject, body: template.body });
      }
      setHasSettingsChanges(false);
      setHasTemplateChanges(false);
    }
  }, [config, selectedLang]);

  // Mutations
  const updateSettingsMutation = useMutation({
    mutationFn: (newSettings: EmailSettings) => invoicesApi.updateEmailSettings(newSettings),
    onSuccess: () => {
      setHasSettingsChanges(false);
      setSaveSuccess('settings');
      setTimeout(() => setSaveSuccess(null), 2000);
      refetch();
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: ({ langIso, template }: { langIso: string; template: { subject: string; body: string } }) =>
      invoicesApi.updateEmailTemplate(langIso, template),
    onSuccess: () => {
      setHasTemplateChanges(false);
      setSaveSuccess('template');
      setTimeout(() => setSaveSuccess(null), 2000);
      refetch();
    },
  });

  const resetTemplateMutation = useMutation({
    mutationFn: (langIso: string) => invoicesApi.resetEmailTemplate(langIso),
    onSuccess: (data) => {
      if (data.template) {
        setCurrentTemplate({ subject: data.template.subject, body: data.template.body });
      }
      setHasTemplateChanges(false);
      setSaveSuccess('reset');
      setTimeout(() => setSaveSuccess(null), 2000);
      refetch();
    },
  });

  const handleSettingsChange = (key: keyof EmailSettings, value: boolean | string) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasSettingsChanges(true);
  };

  const handleTemplateChange = (key: 'subject' | 'body', value: string) => {
    setCurrentTemplate(prev => ({ ...prev, [key]: value }));
    setHasTemplateChanges(true);
  };

  const handleSaveSettings = () => {
    updateSettingsMutation.mutate(settings);
  };

  const handleSaveTemplate = () => {
    updateTemplateMutation.mutate({
      langIso: selectedLang,
      template: currentTemplate
    });
  };

  const handleResetTemplate = () => {
    if (confirm(`Restablecer la plantilla de ${selectedLang.toUpperCase()} a los valores por defecto?`)) {
      resetTemplateMutation.mutate(selectedLang);
    }
  };

  const handleLangChange = (langIso: string) => {
    // Warn about unsaved changes
    if (hasTemplateChanges) {
      if (!confirm('Tienes cambios sin guardar. Deseas continuar?')) {
        return;
      }
    }
    setSelectedLang(langIso);
    setHasTemplateChanges(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-3">
            <Mail className="text-primary-600" size={24} />
            <div>
              <h2 className="text-lg font-bold text-gray-900">Configuracion de Email</h2>
              <p className="text-sm text-slate-500">Personaliza el email de factura para cada idioma</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-primary-600" size={32} />
            </div>
          ) : config ? (
            <div className="space-y-6">
              {/* General Settings */}
              <div className="bg-slate-50 rounded-lg p-4 border border-gray-200">
                <h3 className="text-gray-900 font-medium mb-4 flex items-center gap-2">
                  <FileText size={18} />
                  Configuracion General
                </h3>

                <div className="space-y-4">
                  {/* Attach PDF checkbox */}
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.attach_pdf}
                      onChange={(e) => handleSettingsChange('attach_pdf', e.target.checked)}
                      className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-gray-900 font-medium">Adjuntar PDF de factura</span>
                      <p className="text-sm text-slate-500">Si esta desactivado, el email solo contendra el texto sin el PDF adjunto</p>
                    </div>
                  </label>

                  {/* From Name */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre del remitente
                      </label>
                      <input
                        type="text"
                        value={settings.from_name}
                        onChange={(e) => handleSettingsChange('from_name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="JVS Informatica"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email del remitente
                      </label>
                      <input
                        type="email"
                        value={settings.from_email}
                        onChange={(e) => handleSettingsChange('from_email', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="facturacion@jvs-informatica.com"
                      />
                    </div>
                  </div>

                  {/* Save Settings Button */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSaveSettings}
                      disabled={!hasSettingsChanges || updateSettingsMutation.isPending}
                      className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2 text-sm"
                    >
                      {updateSettingsMutation.isPending ? (
                        <Loader2 className="animate-spin" size={16} />
                      ) : saveSuccess === 'settings' ? (
                        <Check size={16} />
                      ) : (
                        <Save size={16} />
                      )}
                      {saveSuccess === 'settings' ? 'Guardado!' : 'Guardar configuracion'}
                    </button>
                    {hasSettingsChanges && (
                      <span className="text-sm text-yellow-600 flex items-center gap-1">
                        <AlertCircle size={14} />
                        Cambios sin guardar
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Language Selector */}
              <div className="bg-slate-50 rounded-lg p-4 border border-gray-200">
                <h3 className="text-gray-900 font-medium mb-4 flex items-center gap-2">
                  <Globe size={18} />
                  Plantillas por Idioma
                </h3>

                {/* Language tabs */}
                <div className="flex gap-2 mb-4 flex-wrap">
                  {config.templates.map((template) => (
                    <button
                      key={template.lang_iso}
                      onClick={() => handleLangChange(template.lang_iso)}
                      className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors ${
                        selectedLang === template.lang_iso
                          ? 'bg-primary-600 text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-slate-100'
                      }`}
                    >
                      <span className="text-lg">{template.lang_iso === 'es' ? 'ES' : template.lang_iso === 'pt' ? 'PT' : template.lang_iso === 'en' ? 'EN' : template.lang_iso === 'fr' ? 'FR' : 'DE'}</span>
                      {template.lang_name}
                    </button>
                  ))}
                </div>

                {/* Template editor */}
                <div className="space-y-4">
                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Asunto del email
                    </label>
                    <input
                      type="text"
                      value={currentTemplate.subject}
                      onChange={(e) => handleTemplateChange('subject', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                      placeholder="Factura {serie}-{codigo} - Pedido #{order_id}"
                    />
                  </div>

                  {/* Body */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cuerpo del email
                    </label>
                    <textarea
                      value={currentTemplate.body}
                      onChange={(e) => handleTemplateChange('body', e.target.value)}
                      rows={12}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                      placeholder="Escribe el contenido del email..."
                    />
                  </div>

                  {/* Variables help */}
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <div className="flex items-start gap-2">
                      <Info size={16} className="text-primary-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-blue-800 mb-1">Variables disponibles:</p>
                        <div className="flex flex-wrap gap-2">
                          {config.available_variables.map((v) => (
                            <span
                              key={v.name}
                              className="bg-white px-2 py-1 rounded text-xs font-mono text-blue-700 border border-blue-200"
                              title={v.description}
                            >
                              {v.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Template actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSaveTemplate}
                      disabled={!hasTemplateChanges || updateTemplateMutation.isPending}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2 text-sm"
                    >
                      {updateTemplateMutation.isPending ? (
                        <Loader2 className="animate-spin" size={16} />
                      ) : saveSuccess === 'template' ? (
                        <Check size={16} />
                      ) : (
                        <Save size={16} />
                      )}
                      {saveSuccess === 'template' ? 'Guardado!' : `Guardar plantilla (${selectedLang.toUpperCase()})`}
                    </button>

                    <button
                      onClick={handleResetTemplate}
                      disabled={resetTemplateMutation.isPending}
                      className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg flex items-center gap-2 text-sm"
                    >
                      {resetTemplateMutation.isPending ? (
                        <Loader2 className="animate-spin" size={16} />
                      ) : saveSuccess === 'reset' ? (
                        <Check size={16} />
                      ) : (
                        <RotateCcw size={16} />
                      )}
                      Restablecer valores por defecto
                    </button>

                    {hasTemplateChanges && (
                      <span className="text-sm text-yellow-600 flex items-center gap-1">
                        <AlertCircle size={14} />
                        Cambios sin guardar
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <AlertCircle size={48} className="mx-auto mb-4 opacity-50" />
              <p>Error cargando la configuracion</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-slate-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
